<template>
  <div v-if="isLibraryPage">
    <!--
     Direct mappings between the two sets of announcements alleviates reactive
     challenges between DOM changes and the reactive data. Attributes like
     client width, will update outside of the Vue lifecycle hooks and creates
     invalid states. This split removes that obstacle when mapped separately.
    -->
    <announcement-banner-content v-if="showHomepageAnnouncementBanner"
                                 :announcements="homepageAnnouncementsToDisplay"
                                 :key="'homepage-banner'" />
    <announcement-banner-content v-else-if="showLibraryAnnouncementBanner"
                                 :announcements="libraryAnnouncementsToDisplay"
                                 :key="'library-banner'" />
  </div>
</template>

<script setup lang="ts">
import AnnouncementBannerContent
  from '@/components/common/announcement/AnnouncementBannerContent.vue'
import { AnnouncementLibraryResponse } from '@/interfaces/library/announcement';
import {computed, watch} from 'vue';
import { WidgetRootBreadcrumbs } from '@/interfaces/global';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useWidgetNavigator } from '@/components/widgets/composables/useWidgetNavigator';
import i18n from "@/locales";

const store = useStore();
const route = useRoute();
const router = useRouter();
const { currentWidgetView } = useWidgetNavigator();
const isWidget: boolean = store.getters['widgets/isWidget'];

const libraryExceptions = [
  'Exporter',
  'JobManager',
  'Notification', // News Items
  'Notifications', // News Items
  'Order',
  'Orders',
  'OrderSubmitted',
  'Preferences', // Settings
  'PurchaseOrder',
  'Profile', // Settings
  'ReportsManager', // Data Insights
  'RequestForQuote'
];

// Determines if the announcement system should be active on this type of page
const isLibraryPage = computed(() => {
  const currentRouteName = typeof route.name === 'string' ? route.name : '';
  const isLibrary = !route.path.startsWith('/admin');
  return isLibrary && !libraryExceptions.includes(currentRouteName);
});

const localeCode = computed(() => { return i18n.global.locale })

const isRouteAtHomePage = computed(() => {
  switch (isWidget) {
    case true: {
      return currentWidgetView.value === WidgetRootBreadcrumbs.home;
    }
    case false: {
      return router.currentRoute.value.name === 'Home';
    }
  }
});

const homepageAnnouncementsToDisplay = computed<AnnouncementLibraryResponse[]>(() => {
  return store.getters['libraryAnnouncements/homepageAnnouncements'] || [];
});

const libraryAnnouncementsToDisplay = computed<AnnouncementLibraryResponse[]>(() => {
  return store.getters['libraryAnnouncements/libraryAnnouncements'] || [];
});

const showHomepageAnnouncementBanner = computed<boolean>(() => {
  return isRouteAtHomePage.value && homepageAnnouncementsToDisplay.value.length > 0;
})

const showLibraryAnnouncementBanner = computed<boolean>(() => {
  return !isRouteAtHomePage.value && libraryAnnouncementsToDisplay.value.length > 0;
})

watch(
  () => localeCode.value,
  async (value, oldValue) => {
    if (oldValue !== value) {
      await store.dispatch('libraryAnnouncements/initialize')
    }
  }
)
</script>
